import { useEffect, useRef, useState } from 'react';
import { PushPin, Trash, SmileyWink, DownloadSimple, FileArrowDown, ArrowBendUpLeft, PencilSimple, Check, X, Sparkle } from '@phosphor-icons/react';
import { formatShort } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUICK_EMOJI = ['👍', '❤️', '😄', '🎉', '🙏', '✅'];

function fmtTime(iso) {
  return formatShort(iso);
}

function renderText(text) {
  // Highlight @mentions
  const parts = String(text || '').split(/(@[^\s@]+)/g);
  return parts.map((p, i) =>
    p.startsWith('@')
      ? <span key={`${i}-${p}`} className="text-teal-700 font-medium bg-blue-50 rounded px-0.5">{p}</span>
      : <span key={`${i}-${p}`}>{p}</span>);
}

function MessageItem({ msg, currentUser, onReact, onPin, onDelete, onReply, onEdit, onAskAi }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.text || '');
  const mine = msg.user_id === currentUser?.id;
  const canDelete = mine || currentUser?.role === 'admin';
  const initial = (msg.user_name || '?').charAt(0).toUpperCase();

  const saveEdit = async () => {
    const t = draft.trim();
    if (!t || t === msg.text) { setEditing(false); return; }
    await onEdit(msg.id, t);
    setEditing(false);
  };

  return (
    <div className="group flex gap-3 px-4 py-2 hover:bg-zinc-50/70 relative msg-enter" data-testid={`chat-message-${msg.id}`}>
      <span className="shrink-0 w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 text-xs font-bold flex items-center justify-center mt-0.5">
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-900"><span data-no-translate>{msg.user_name}</span></span>
          <span className="text-[11px] text-zinc-400">{fmtTime(msg.created_at)}</span>
          {msg.edited_at && <span className="text-[10px] text-zinc-400">（編集済み）</span>}
          {msg.pinned && <PushPin size={12} weight="fill" className="text-amber-500" />}
        </div>

        {msg.reply_preview && (
          <div className="flex items-center gap-1 mt-1 mb-0.5 pl-2 border-l-2 border-zinc-300 text-[11px] text-zinc-500" data-testid={`reply-quote-${msg.id}`}>
            <ArrowBendUpLeft size={11} className="shrink-0" />
            <b className="text-zinc-600"><span data-no-translate>{msg.reply_preview.user_name}</span></b>
            <span className="truncate">{msg.reply_preview.text}</span>
          </div>
        )}

        {editing ? (
          <div className="mt-1 flex items-start gap-1.5">
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
              data-testid={`edit-input-${msg.id}`}
              className="flex-1 resize-none px-2 py-1.5 rounded-lg border border-teal-700 text-sm focus:outline-none focus:ring-2 focus:ring-teal-700/30" autoFocus />
            <button type="button" onClick={saveEdit} data-testid={`edit-save-${msg.id}`} className="p-1.5 rounded bg-teal-700 text-white hover:bg-blue-700"><Check size={15} /></button>
            <button type="button" onClick={() => { setEditing(false); setDraft(msg.text || ''); }} className="p-1.5 rounded text-zinc-500 hover:bg-zinc-100"><X size={15} /></button>
          </div>
        ) : (
          msg.text && <div className="text-sm text-zinc-800 whitespace-pre-wrap break-words mt-0.5">{renderText(msg.text)}</div>
        )}

        {(msg.attachments || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {msg.attachments.map((a) => (
              a.is_image ? (
                <a key={a.file_id} href={`${API}/chat/attachments/${a.file_id}`} target="_blank" rel="noreferrer"
                   data-testid={`chat-attachment-${a.file_id}`}>
                  <img src={`${API}/chat/attachments/${a.file_id}`} alt={a.name}
                       className="max-h-48 max-w-[260px] rounded-lg border border-zinc-200 object-cover" />
                </a>
              ) : (
                <a key={a.file_id} href={`${API}/chat/attachments/${a.file_id}`} target="_blank" rel="noreferrer"
                   data-testid={`chat-attachment-${a.file_id}`}
                   className="inline-flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 rounded-lg px-3 py-2 text-sm text-zinc-700 transition-colors">
                  <FileArrowDown size={18} className="text-zinc-500" />
                  <span className="max-w-[180px] truncate">{a.name}</span>
                  <DownloadSimple size={14} className="text-zinc-400" />
                </a>
              )
            ))}
          </div>
        )}

        {Object.keys(msg.reactions || {}).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {Object.entries(msg.reactions).map(([emoji, users]) => (
              <button key={emoji} type="button" onClick={() => onReact(msg.id, emoji)}
                data-testid={`reaction-${msg.id}-${emoji}`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs transition-colors ${
                  users.includes(currentUser?.id) ? 'border-teal-700 bg-teal-700/10' : 'border-zinc-200 bg-white hover:bg-zinc-50'
                }`}>
                <span>{emoji}</span><span className="text-zinc-600">{users.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Hover actions */}
      <div className="absolute right-3 top-1 hidden group-hover:flex items-center gap-0.5 bg-white border border-zinc-200 rounded-md shadow-sm">
        <button type="button" onClick={() => onReply(msg)} data-testid={`reply-btn-${msg.id}`}
          className="p-1.5 text-zinc-500 hover:text-teal-700 hover:bg-zinc-100 rounded">
          <ArrowBendUpLeft size={16} />
        </button>
        {onAskAi && msg.text && (
          <button type="button" onClick={() => onAskAi(msg)} data-testid={`ask-ai-btn-${msg.id}`}
            className="p-1.5 text-zinc-500 hover:text-violet-600 hover:bg-zinc-100 rounded" title="AIに質問">
            <Sparkle size={16} />
          </button>
        )}
        {mine && (
          <button type="button" onClick={() => { setDraft(msg.text || ''); setEditing(true); }} data-testid={`edit-btn-${msg.id}`}
            className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded">
            <PencilSimple size={16} />
          </button>
        )}
        <div className="relative">
          <button type="button" onClick={() => setShowEmoji((s) => !s)} data-testid={`react-btn-${msg.id}`}
            className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded">
            <SmileyWink size={16} />
          </button>
          {showEmoji && (
            <div className="absolute right-0 top-full mt-1 flex gap-0.5 bg-white border border-zinc-200 rounded-lg shadow-lg p-1 z-20">
              {QUICK_EMOJI.map((e) => (
                <button key={e} type="button" onClick={() => { onReact(msg.id, e); setShowEmoji(false); }}
                  className="w-7 h-7 hover:bg-zinc-100 rounded text-base">{e}</button>
              ))}
            </div>
          )}
        </div>
        <button type="button" onClick={() => onPin(msg.id)} data-testid={`pin-btn-${msg.id}`}
          className="p-1.5 text-zinc-500 hover:text-amber-600 hover:bg-zinc-100 rounded">
          <PushPin size={16} weight={msg.pinned ? 'fill' : 'regular'} />
        </button>
        {canDelete && (
          <button type="button" onClick={() => onDelete(msg.id)} data-testid={`delete-btn-${msg.id}`}
            className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 rounded">
            <Trash size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function MessageList({ messages, currentUser, channelId, onReact, onPin, onDelete, onReply, onEdit, onAskAi }) {
  const containerRef = useRef(null);
  const endRef = useRef(null);
  const stickRef = useRef(true);
  const prevLenRef = useRef(0);
  const prevChanRef = useRef(channelId);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    // "stuck to bottom" if within 100px of the end
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  useEffect(() => {
    // On channel switch, jump straight to the latest message.
    if (prevChanRef.current !== channelId) {
      prevChanRef.current = channelId;
      stickRef.current = true;
      prevLenRef.current = messages.length;
      endRef.current?.scrollIntoView({ behavior: 'auto' });
      return;
    }
    const grew = messages.length > prevLenRef.current;
    prevLenRef.current = messages.length;
    // Only auto-scroll when the user is already at the bottom — never yank them
    // up while they're reading history during the 2.5s poll.
    if (stickRef.current) {
      endRef.current?.scrollIntoView({ behavior: grew ? 'smooth' : 'auto' });
    }
  }, [messages, channelId]);

  if (!messages.length) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400" data-testid="chat-empty">
        まだメッセージがありません。最初のメッセージを送ってみましょう。
      </div>
    );
  }
  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto py-2" data-testid="chat-message-list">
      {messages.map((m) => (
        <MessageItem key={m.id} msg={m} currentUser={currentUser} onReact={onReact} onPin={onPin} onDelete={onDelete} onReply={onReply} onEdit={onEdit} onAskAi={onAskAi} />
      ))}
      <div ref={endRef} />
    </div>
  );
}
